import axios from 'axios';
import _ from 'lodash';
import {
  helperGetApiHeaders,
  helperGetApiSeriesUrlRoute,
  helperConvertApiRoute,
  apiErrorHandler,
  helperGetApiCategoryUrlRoute,
} from 'helpers/ApiHelper';
import { fetchFooter } from 'components/Footer/FooterActions';
import { NATION_WATCH_FREE, PANEL_RECOMMENDED_SERIES } from 'helpers/ContentHelper';
import {
  getTabDataUrl,
  isContinueWatchingComponent,
  isInlineComponent,
} from 'helpers/SeriesDetailHelpers';
import { fetchVideosTab } from 'screens/ContentDetail/components/VideosTab/VideosTabActions';

export const FETCH_SERIES_URL = 'FETCH_SERIES_URL';
export const FETCH_SERIES_DETAIL = 'FETCH_SERIES_DETAIL';
export const SET_SERIES_DETAIL_TABS = 'SET_SERIES_DETAIL_TABS';
export const CLEAR_SERIES_DETAIL = 'CLEAR_SERIES_DETAIL';
export const SET_SERIES_DETAIL_ERROR = 'SET_SERIES_DETAIL_ERROR';
export const FETCH_DETAIL_CATEGORY = 'FETCH_DETAIL_CATEGORY';
export const FETCH_EPISODE_DETAIL = 'FETCH_EPISODE_DETAIL';
export const FETCH_DETAIL_CATEGORY_ANONYMOUS = 'FETCH_DETAIL_CATEGORY_ANONYMOUS';

function fetchSeriesUrl(showId, accessToken, deviceId) {
  const pageType = 'showsQuery';
  const request = axios
    .get(
      helperGetApiSeriesUrlRoute({ showId }),
      helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }),
    )
    .catch(e => apiErrorHandler({ error: e }));
  return {
    type: FETCH_SERIES_URL,
    payload: request,
  };
}

function fetchEpisodes(url, accessToken, deviceId) {
  const pageType = 'seriesDetail';
  const request = axios
    .get(url, helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }))
    .catch(e => apiErrorHandler({ error: e }));
  return {
    type: FETCH_EPISODE_DETAIL,
    payload: request,
  };
}

function fetchCategoryDetailUrl(id, accessToken, deviceId) {
  const pageType = 'category';
  const route = helperGetApiCategoryUrlRoute({ id });
  const request = axios
    .get(route, helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }))
    .catch(e => apiErrorHandler({ error: e }));
  return {
    type: FETCH_DETAIL_CATEGORY,
    payload: request,
  };
}

function fetchAnonymousCategoryDetailUrl(id, accessToken, deviceId) {
  const pageType = 'category';
  const route = helperGetApiCategoryUrlRoute({ id });
  const request = axios
    .get(route, helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }))
    .catch(e => apiErrorHandler({ error: e }));
  return {
    type: FETCH_DETAIL_CATEGORY_ANONYMOUS,
    payload: request,
  };
}

export function fetchAnonymousCategoryDetail(id, accessToken, deviceId) {
  return dispatch =>
    dispatch(fetchAnonymousCategoryDetailUrl(id, accessToken, deviceId)).then(() =>
      dispatch(fetchFooter()),
    );
}

function fetchSeriesDetail(seriesScreenUrl, accessToken, deviceId) {
  const pageType = 'seriesDetail';
  const request = axios
    .get(
      helperConvertApiRoute(seriesScreenUrl),
      helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }),
    )
    .catch(e => apiErrorHandler({ error: e }));

  return {
    type: FETCH_SERIES_DETAIL,
    payload: request,
  };
}

export function setSeriesDetailError(error) {
  const payload = {
    response: {
      status: error,
    },
  };

  return {
    type: SET_SERIES_DETAIL_ERROR,
    payload,
  };
}

export function clearSeriesDetail() {
  return {
    type: CLEAR_SERIES_DETAIL,
  };
}

function setSeriesDetailTabs(tabs) {
  return {
    type: SET_SERIES_DETAIL_TABS,
    payload: tabs,
  };
}

export function fetchSeries(showId, accessToken, deviceId, fetchSeasonNumber) {
  return dispatch =>
    dispatch(fetchSeriesUrl(showId, accessToken, deviceId))
      .then(response => {
        const seriesScreenUrl = _.get(response, 'payload.data.screenUrl', null);
        if (!seriesScreenUrl) return null;
        return dispatch(fetchSeriesDetail(seriesScreenUrl, accessToken, deviceId)).then(
          response => {
            const panels = _.get(response, 'payload.data.panels', null);
            if (!panels) return null;
            const seasonPanel = _.get(panels, 'member[1].items.member');
            const filterSeasonPanel = seasonPanel?.filter(
              seasons => seasons.seasonNumber === fetchSeasonNumber,
            );
            const currentSeasonPanel = fetchSeasonNumber ? filterSeasonPanel : seasonPanel;
            if (!currentSeasonPanel) return null;
            _.forEach(currentSeasonPanel, panel => {
              const isPanelSeason = panel['@type'] === 'Season';
              if (isPanelSeason) {
                const episodeUrl = panel.episodes['@id'];
                return dispatch(fetchEpisodes(episodeUrl, accessToken, deviceId));
              }
              return panel;
            });
            return response;
          },
        );
      })
      .then(() => dispatch(fetchFooter()));
}

export function fetchCategoryDetail(id, accessToken, deviceId) {
  return dispatch =>
    dispatch(fetchCategoryDetailUrl(id, accessToken, deviceId)).then(() => dispatch(fetchFooter()));
}

export function fetchAnonymousSeries(showId, accessToken, deviceId) {
  return dispatch =>
    dispatch(fetchSeriesUrl(showId, accessToken, deviceId))
      .then(response => {
        const seriesScreenUrl = _.get(response, 'payload.data.screenUrl', null);
        if (!seriesScreenUrl) return null;
        return dispatch(fetchSeriesDetail(seriesScreenUrl, accessToken, deviceId)).then(result => {
          const data = result?.payload?.data;
          const panels = data?.panels?.member ?? null;
          const shouldDisplayAllPanels = data?.['@id']?.includes(NATION_WATCH_FREE);
          const tabComponents =
            panels &&
            panels?.filter(elem => !isInlineComponent(elem) && !isContinueWatchingComponent(elem));
          const tabItems = _.get(tabComponents, '0.items.member', null);
          const featuredTabs = tabComponents?.filter(
            tabItem =>
              tabItem?.displayTemplate === 'featured' ||
              tabItem?.headline?.toLowerCase() === PANEL_RECOMMENDED_SERIES,
          );
          let tab;
          const tabs = [shouldDisplayAllPanels ? Object.assign(tab, ...tabItems) : tabItems?.[0]];
          if (!shouldDisplayAllPanels && featuredTabs.length) tabs.push(...featuredTabs);
          if (!tabs) return null;
          return Promise.all(
            tabs.map((tab, index) => {
              const url = getTabDataUrl(tab);
              if (!url) return Promise.resolve();
              return dispatch(fetchVideosTab({ url, accessToken })).then(response => {
                const data = _.get(response, 'payload.data');
                _.set(tabs, `${index}.items`, data);
                return Promise.resolve();
              });
            }),
          ).then(() => dispatch(setSeriesDetailTabs(tabs)));
        });
      })
      .then(() => dispatch(fetchFooter()));
}
